import { useEffect, useMemo, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import { useMemberId } from '@trello/authentication';
import type { TouchpointSourceType } from '@trello/cross-flow';
import type { ExperimentVariations } from '@trello/feature-gate-client';

import { trelloCrossFlowOpen, type TrelloCrossFlow } from '../TrelloCrossFlow';
import { useHasEligibleWorkspaces } from './runtimeEligibilityChecks/useHasEligibleWorkspaces';
import { useHasNoAvailableSites } from './runtimeEligibilityChecks/useHasNoAvailableSites';
import { useIsCrossFlowMemberConfirmed } from './runtimeEligibilityChecks/useIsCrossFlowMemberConfirmed';
import { useIsEnterpriseAdminMember } from './runtimeEligibilityChecks/useIsEnterpriseAdminMember';
import { useEligibleEnterpriseWorkspacesQuery } from './EligibleEnterpriseWorkspacesQuery.generated';
import { useMemberEnterprisesFragment } from './MemberEnterprisesFragment.generated';
import { useTrelloCrossFlowExperiment } from './useTrelloCrossFlowExperiment';

const experimentFeatureKey = 'xf_enterprise_self_serve';

export type CrossFlowForEnterpriseSelfServeExperimentVariations =
  ExperimentVariations<typeof experimentFeatureKey, 'cohort'>;

export interface CrossFlowForEnterpriseSelfServeParams {
  source: TouchpointSourceType;
  workspaceId?: string;
  boardId?: string;
  enterpriseId?: string;
  hasValidAaSession: boolean;
}

export const useCrossFlowForEnterpriseSelfServe = ({
  source,
  workspaceId,
  enterpriseId,
  hasValidAaSession,
}: CrossFlowForEnterpriseSelfServeParams): TrelloCrossFlow<CrossFlowForEnterpriseSelfServeExperimentVariations> => {
  const crossFlow = useCrossFlow();
  const memberId = useMemberId();

  // 1. Check that the member is confirmed
  const { isEligible: isMemberConfirmed } = useIsCrossFlowMemberConfirmed();

  // 2. Check that the member has eligible workspaces
  const { eligibleWorkspaceOptions } = useHasEligibleWorkspaces({
    workspaceId,
    enterpriseId,
    entitlementRequired: 'enterprise',
  });

  const [
    isImmediateRuntimeEligibilityChecksPassed,
    setImmediateRuntimeEligibilityChecksPassed,
  ] = useState(false);
  // 4. Check that the member has no available sites
  const { isEligible: hasNoAvailableSites } = useHasNoAvailableSites({
    doLoadAvailableSites: isImmediateRuntimeEligibilityChecksPassed,
  });

  const { data: memberEnterprisesData } = useMemberEnterprisesFragment({
    from: { id: memberId },
  });

  const memberEnterprises = memberEnterprisesData?.enterprises ?? [];
  /**
   * If there are more than one enterprise the eligibility check below fails.
   */
  const memberEnterpriseId = memberEnterprises[0]?.id;

  const { isAdmin } = useIsEnterpriseAdminMember({
    enterpriseId: enterpriseId || memberEnterpriseId || '',
  });

  const hasEnterpriseId = !!enterpriseId || !!memberEnterpriseId;
  const { data } = useEligibleEnterpriseWorkspacesQuery({
    variables: {
      enterpriseId: enterpriseId || memberEnterpriseId || '',
      startIndex: 1,
      /**
       * For the first phase of enterprise self serve we are only targetting
       * enterprises with < 20 workspaces.
       */
      count: 25,
    },
    waitOn: ['None'],
    skip: !isAdmin || !hasEnterpriseId,
  });

  const adminEnterpriseWorkspaces =
    data?.enterprise?.organizations?.organizations ?? [];

  const eligibleAdminEnterpriseWorkspaces = adminEnterpriseWorkspaces.filter(
    (workspace) => !workspace.jwmLink?.idCloud,
  );

  const workspaceOptions =
    isAdmin && eligibleAdminEnterpriseWorkspaces.length > 0
      ? eligibleAdminEnterpriseWorkspaces.map((org) => ({
          id: org.id,
          displayName: org.displayName,
        }))
      : eligibleWorkspaceOptions;

  const experiment = useTrelloCrossFlowExperiment({
    experimentFeatureKey,
    source,
    eligibilityCheckResults: [
      crossFlow.isEnabled,
      isMemberConfirmed,
      workspaceOptions?.length > 0,
      /**
       * For the purpose of the pilot program we want to target only
       * users who are only part of one enterprise so that we don't
       * accidentally create linked jira sites for other enterprises.
       * checking 2 here with `hasEnterpiseId` will ensure that if a user does
       * not have an enterprise license, but is owned by an enterprise, they will
       * be eligible.
       */
      memberEnterprises.length < 2,
      hasEnterpriseId,
    ],
    deferredEligibilityCheckResults: [hasNoAvailableSites],
  });

  useEffect(() => {
    setImmediateRuntimeEligibilityChecksPassed(
      experiment.isImmediateRuntimeEligibilityChecksPassed,
    );
  }, [experiment.isImmediateRuntimeEligibilityChecksPassed]);

  return useMemo(
    () => ({
      ...experiment,
      open: trelloCrossFlowOpen(
        crossFlow,
        source,
        workspaceOptions,
        hasValidAaSession,
        {
          workspaceId,
          isEnterpriseSelfServe: true,
          isJiraPremiumTrial: true,
        },
      ),
    }),
    [
      experiment,
      crossFlow,
      source,
      workspaceOptions,
      workspaceId,
      hasValidAaSession,
    ],
  );
};
